/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * A miniature of one TV view, drawn at 16:9 in the theme it will actually wear
 * on the wall. Not an icon — the real chrome (workspace pill, period strip,
 * scene pips) and the real content shapes, so a view is recognisable at a
 * glance the way it is across the room.
 *
 * Built-in view  → the team leaderboard rows.
 * Dashboard view → its components, sketched from what the board holds.
 */

const CSS = `
.tvs{ position:relative; aspect-ratio:16/9; overflow:hidden; border-radius:9px;
  background:radial-gradient(60% 60% at 50% 40%, #1B2740 0%, #0B1320 100%);
  box-shadow:0 3px 14px -5px rgba(11,19,32,0.34), inset 0 0 0 1px rgba(255,255,255,0.06); }
.tvs.lt{ background:radial-gradient(60% 60% at 50% 40%, #F4F6FB 0%, #E6EAF2 100%);
  box-shadow:0 3px 14px -5px rgba(16,24,40,0.20), inset 0 0 0 1px rgba(15,23,42,0.07); }
.tvs-bar{ position:absolute; top:0; left:0; right:0; height:13%; display:flex; align-items:center;
  justify-content:space-between; padding:0 3%; z-index:3; }
.tvs-ws{ display:flex; align-items:center; gap:5px; background:rgba(255,255,255,0.09);
  border:1px solid rgba(255,255,255,0.10); border-radius:6px; padding:3px 8px; }
.tvs.lt .tvs-ws{ background:#fff; border-color:rgba(15,23,42,0.08); }
.tvs-ws i{ width:11px; height:11px; border-radius:3px; background:linear-gradient(140deg,#2E4A78,#16233C); flex:none; display:block; }
.tvs.lt .tvs-ws i{ background:linear-gradient(140deg,#C9D6EA,#9FB3D2); }
.tvs-ws b{ font-size:8px; font-weight:600; color:#F5F7FB; letter-spacing:-0.01em; }
.tvs.lt .tvs-ws b{ color:#0B1320; }
.tvs-per{ display:flex; gap:3px; }
.tvs-per i{ display:block; width:17px; height:5px; border-radius:3px; background:rgba(255,255,255,0.10); }
.tvs-per i.on{ background:var(--gmb-primary, #1B598E); opacity:0.55; width:23px; }
.tvs.lt .tvs-per i{ background:rgba(15,23,42,0.10); }
.tvs-stage{ position:absolute; inset:15% 3% 8%; }
.tvs-ttl{ font-size:9.5px; font-weight:600; color:#F5F7FB; margin-bottom:6px; letter-spacing:-0.01em; }
.tvs.lt .tvs-ttl{ color:#0B1320; }
.tvs-pips{ position:absolute; bottom:3.4%; left:50%; transform:translateX(-50%); display:flex; gap:3px; z-index:3; }
.tvs-pips i{ width:4px; height:4px; border-radius:999px; background:rgba(255,255,255,0.20); display:block; }
.tvs-pips i.on{ width:14px; background:var(--gmb-primary, #1B598E); }
.tvs.lt .tvs-pips i{ background:rgba(15,23,42,0.18); }
.tvs-live{ position:absolute; left:9px; bottom:9px; z-index:5; display:inline-flex; align-items:center; gap:5px;
  background:rgba(11,19,32,0.6); -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px);
  border:1px solid rgba(255,255,255,0.14); border-radius:999px; padding:3px 9px 3px 7px;
  font-size:9px; font-weight:600; color:#fff; }
.tvs.lt .tvs-live{ background:rgba(255,255,255,0.84); border-color:rgba(15,23,42,0.10); color:#0B1320; }
.tvs-live i{ width:4.5px; height:4.5px; border-radius:999px; background:#12A150; display:block;
  box-shadow:0 0 0 3px rgba(18,161,80,0.22); }

.tvs-row{ display:flex; align-items:center; gap:6px; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.06); border-radius:5px; padding:4px 8px; margin-bottom:3px; }
.tvs.lt .tvs-row{ background:#fff; border-color:rgba(15,23,42,0.06); }
.tvs-rk{ width:12px; height:12px; border-radius:3px; display:grid; place-items:center; font-size:7px; font-weight:600;
  background:rgba(255,255,255,0.09); color:rgba(245,247,251,0.65); flex:none; }
.tvs.lt .tvs-rk{ background:rgba(15,23,42,0.06); color:#667085; }
.tvs-rk.g{ background:#F0B33D; color:#3A2A05; }
.tvs-av{ width:13px; height:13px; border-radius:999px; flex:none; background:linear-gradient(140deg,#3E5680,#25344F); }
.tvs.lt .tvs-av{ background:linear-gradient(140deg,#C9D6EA,#A9BBD6); }
.tvs-nm{ flex:1; height:4px; border-radius:3px; background:rgba(255,255,255,0.16); }
.tvs.lt .tvs-nm{ background:rgba(15,23,42,0.13); }
.tvs-pt{ font-size:8px; font-weight:600; color:#F5F7FB; font-variant-numeric:tabular-nums; flex:none; }
.tvs.lt .tvs-pt{ color:#0B1320; }

.tvs-mtg{ display:grid; grid-template-columns:repeat(3,1fr); gap:5px; margin-bottom:5px; }
.tvs-mt{ position:relative; height:38px; border-radius:5px; overflow:hidden;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.06); }
.tvs.lt .tvs-mt{ background:#fff; border-color:rgba(15,23,42,0.06); }
.tvs-mt svg{ position:absolute; left:36%; top:-14%; width:80%; height:150%; }
.tvs-mv{ position:absolute; left:6px; top:5px; font-size:9.5px; font-weight:600; color:#F5F7FB; letter-spacing:-0.02em; z-index:2; }
.tvs.lt .tvs-mv{ color:#0B1320; }
.tvs-ml{ position:absolute; left:6px; top:18px; font-size:6px; font-weight:500; color:rgba(245,247,251,0.45); z-index:2; }
.tvs.lt .tvs-ml{ color:rgba(11,19,32,0.42); }
.tvs-dot{ position:absolute; width:5px; height:5px; border-radius:999px; transform:translate(-50%,-50%);
  border:1.2px solid #0F1A2C; z-index:2; }
.tvs.lt .tvs-dot{ border-color:#fff; }
.tvs-dot.you{ background:#8FA8FF; } .tvs.lt .tvs-dot.you{ background:#16007a; }
.tvs-dot.yeti{ background:#ff1d8f; }
`;

// The Figma climb line + two ridges (BeatTheYeti node 61:188), drawn small.
const LINE = "M4 126.586C4 126.586 36.9903 53.1835 67.3004 76.3801C97.6105 99.5768 88.6034 31.8496 120.254 57.1709C151.904 82.4922 174 24.5861 174 24.5861";
const RIDGE_A = "M65.5345 76.4424C34.1547 51.4264 0 130.586 0 130.586H176V20.5861C176 20.5861 153.124 83.0338 120.357 55.7266C87.5894 28.4193 96.9144 101.458 65.5345 76.4424Z";
const RIDGE_B = "M177.09 145.536L177.09 66.0749L154.198 67.3529C152.688 67.4372 151.234 67.9473 150.003 68.8242L135.43 79.2028L118.527 87.8429C117.729 88.2509 116.868 88.5218 115.98 88.6444L93.6119 91.7315L88.3048 92.879C86.463 93.2772 84.8209 94.3128 83.6679 95.8033L81.4496 98.6708C79.5684 101.102 76.4603 102.241 73.4536 101.6L70.4493 100.959C68.5866 100.562 66.6434 100.842 64.969 101.75L56.9155 106.115C55.8193 106.709 54.5993 107.039 53.353 107.077L38.271 107.548C37.2112 107.581 36.1686 107.824 35.2038 108.264L24.2149 113.27L21.7971 128.192L58.9702 139.109L85.2543 150.618L177.09 145.536Z";

const TILES = [
  { v: "$312K", l: "Revenue", you: 0.78, yeti: 0.75, win: true },
  { v: "54%", l: "Closed rate", you: 0.83, yeti: 0.77, win: true },
  { v: "$1,287", l: "AVG ticket", you: 0.72, yeti: 0.79, win: false },
];
const ROWS = ["4,120", "3,780", "3,145", "2,910"];

export default function TvViewScreen({ view, workspaceName }) {
  /* The card is LIGHT unless the view is pinned to Dark. It sits on a light
     settings page, so a dark miniature made every view look like a night board
     — including the built-in one and every view left on Auto, which are not
     dark at all. Only an explicit "Dark" draws the dark treatment. */
  const dark = view.theme === "dark";
  const ref = useRef(null);

  // Put the two climbers on the real path, same maths the live card uses.
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.querySelectorAll(".tvs-mt").forEach((el) => {
      if (el.dataset.done) return;
      const svg = el.querySelector("svg");
      const path = el.querySelector(".tvs-ln");
      if (!svg || !path || !path.getTotalLength) return;
      const len = path.getTotalLength();
      const box = svg.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      if (!len || !box.width) return;
      [["you", Number(el.dataset.you)], ["yeti", Number(el.dataset.yeti)]].forEach(([kind, frac]) => {
        const pt = path.getPointAtLength(frac * len);
        const d = document.createElement("span");
        d.className = `tvs-dot ${kind}`;
        d.style.left = `${(((box.left - rect.left) + (pt.x / 236) * box.width) / rect.width) * 100}%`;
        d.style.top = `${(((box.top - rect.top) + (pt.y / 221) * box.height) / rect.height) * 100}%`;
        el.appendChild(d);
      });
      el.dataset.done = "1";
    });
  }, [view.id, dark]);

  const gA = dark ? "tvsRidgeAD" : "tvsRidgeAL";
  const gB = dark ? "tvsRidgeBD" : "tvsRidgeBL";

  const rows = (n) =>
    ROWS.slice(0, n).map((pts, i) => (
      <div className="tvs-row" key={pts}>
        <span className={`tvs-rk${i === 0 ? " g" : ""}`}>{i + 1}</span>
        <span className="tvs-av" />
        <span className="tvs-nm" style={{ maxWidth: `${72 - i * 7}%` }} />
        <span className="tvs-pt">{pts}</span>
      </div>
    ));

  return (
    <span ref={ref} className={`tvs${dark ? "" : " lt"}`} style={{ display: "block" }}>
      <style>{CSS}</style>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="tvsRidgeAD" x1="92" y1="34" x2="116" y2="108" gradientUnits="userSpaceOnUse">
            <stop offset="0.017" stopColor="#3A4C6E" /><stop offset="1" stopColor="#22304C" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="tvsRidgeBD" x1="124" y1="41" x2="132" y2="163" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2E3E60" /><stop offset="0.692" stopColor="#1A2438" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="tvsRidgeAL" x1="92" y1="34" x2="116" y2="108" gradientUnits="userSpaceOnUse">
            <stop offset="0.017" stopColor="#DDDDE0" /><stop offset="1" stopColor="#EBEBEF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="tvsRidgeBL" x1="124" y1="41" x2="132" y2="163" gradientUnits="userSpaceOnUse">
            <stop stopColor="#C1C6E4" /><stop offset="0.692" stopColor="#EBECED" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      <span className="tvs-bar">
        <span className="tvs-ws"><i /><b>{workspaceName || "In-House Sales"}</b></span>
        <span className="tvs-per"><i /><i className="on" /><i /></span>
      </span>

      <span className="tvs-stage" style={{ display: "block" }}>
        <span className="tvs-ttl" style={{ display: "block" }}>
          {view.builtin ? "Team Leaderboard" : view.name}
        </span>
        {view.builtin ? (
          rows(4)
        ) : (
          <>
            <span className="tvs-mtg" style={{ display: "grid" }}>
              {TILES.map((t) => (
                <span
                  key={t.l}
                  className="tvs-mt"
                  data-you={t.you}
                  data-yeti={t.yeti}
                  style={{ display: "block" }}
                >
                  <svg viewBox="0 0 236 221" preserveAspectRatio="none">
                    <path d={RIDGE_A} fill={`url(#${gA})`} />
                    <path d={RIDGE_B} fill={`url(#${gB})`} />
                    <path className="tvs-ln" d={LINE} fill="none" stroke={t.win ? "#299E47" : "#D93D42"} strokeWidth="4" strokeLinecap="round" />
                  </svg>
                  <span className="tvs-mv">{t.v}</span>
                  <span className="tvs-ml">{t.l}</span>
                </span>
              ))}
            </span>
            {rows(2)}
          </>
        )}
      </span>

      {view.pages > 1 && (
        <span className="tvs-pips"><i className="on" /><i /></span>
      )}
      <span className="tvs-live"><i />On now</span>
    </span>
  );
}

TvViewScreen.propTypes = {
  view: PropTypes.object.isRequired,
  workspaceName: PropTypes.string,
};
